import React, { useState } from 'react';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';

const SMSFSuccessionPlanning = ({ smsfState, setSmsfState }) => {
    // Current trustees from config
    const [currentTrustees] = useState([
        { name: 'John Smith', age: 65, role: 'Primary Trustee' },
        { name: 'Jane Smith', age: 62, role: 'Trustee' }
    ]);
    
    // Successor trustee
    const [successorName, setSuccessorName] = useState('');
    const [successorAge, setSuccessorAge] = useState('');
    const [successorRelationship, setSuccessorRelationship] = useState('');
    
    // Trigger events
    const [triggerEvents, setTriggerEvents] = useState({
        death: true,
        incapacity: true,
        resignation: false
    });
    
    // Transition plan steps
    const [transitionSteps, setTransitionSteps] = useState('');
    
    // Documents
    const [documents, setDocuments] = useState([]);
    
    // Review schedule
    const [reviewFrequency, setReviewFrequency] = useState('annual');
    const nextReview = new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0];
    
    return (
        <div className="space-y-6 p-6 bg-gray-50 rounded-lg shadow-inner">
            <div>
                <h2 className="text-2xl font-bold text-gray-800">Succession Planning</h2>
                <p className="text-sm text-gray-600 mt-1">Plan for trustee succession and fund continuity</p>
            </div>
            
            {/* Test 887: Current Trustees */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Current Trustees</h3>
                <div className="space-y-3">
                    {currentTrustees.map((trustee, index) => (
                        <div key={index} className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex justify-between items-center">
                            <div>
                                <div className="font-semibold text-blue-800">{trustee.name}</div>
                                <div className="text-sm text-blue-700">{trustee.role}</div>
                            </div>
                            <div className="text-right">
                                <div className="text-lg font-bold text-blue-700">Age {trustee.age}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            
            {/* Test 888: Successor Trustee */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Nominate Successor Trustee</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Successor Name</label>
                        <input
                            type="text"
                            value={successorName}
                            onChange={(e) => setSuccessorName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="Enter name"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Age</label>
                        <input
                            {...getNumericInputProps()}
                            value={successorAge}
                            onChange={(e) => setSuccessorAge(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="e.g., 45"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Relationship</label>
                        <select
                            value={successorRelationship}
                            onChange={(e) => setSuccessorRelationship(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="">Select...</option>
                            <option value="child">Adult Child</option>
                            <option value="spouse">Spouse/Partner</option>
                            <option value="sibling">Sibling</option>
                            <option value="professional">Professional Trustee</option>
                        </select>
                    </div>
                </div>
            </div>
            
            {/* Test 889: Trigger Events */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Trigger Events for Succession</h3>
                <div className="space-y-3">
                    <label className="flex items-center p-3 bg-gray-50 rounded-lg">
                        <input
                            type="checkbox"
                            checked={triggerEvents.death}
                            onChange={(e) => setTriggerEvents({ ...triggerEvents, death: e.target.checked })}
                            className="mr-3 h-4 w-4"
                        />
                        <div>
                            <div className="font-medium">Death of Current Trustee</div>
                            <div className="text-sm text-gray-600">Successor automatically appointed</div>
                        </div>
                    </label>
                    <label className="flex items-center p-3 bg-gray-50 rounded-lg">
                        <input
                            type="checkbox"
                            checked={triggerEvents.incapacity}
                            onChange={(e) => setTriggerEvents({ ...triggerEvents, incapacity: e.target.checked })}
                            className="mr-3 h-4 w-4"
                        />
                        <div>
                            <div className="font-medium">Loss of Capacity/Incapacity</div>
                            <div className="text-sm text-gray-600">Medical certification required</div>
                        </div>
                    </label>
                    <label className="flex items-center p-3 bg-gray-50 rounded-lg">
                        <input
                            type="checkbox"
                            checked={triggerEvents.resignation}
                            onChange={(e) => setTriggerEvents({ ...triggerEvents, resignation: e.target.checked })}
                            className="mr-3 h-4 w-4"
                        />
                        <div>
                            <div className="font-medium">Voluntary Resignation</div>
                            <div className="text-sm text-gray-600">Formal resignation process</div>
                        </div>
                    </label>
                </div>
            </div>
            
            {/* Test 890: Transition Plan */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Transition Plan</h3>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Step-by-Step Transition Process
                        </label>
                        <textarea
                            value={transitionSteps}
                            onChange={(e) => setTransitionSteps(e.target.value)}
                            rows="8"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="1. Notify ATO of trustee change&#10;2. Update SMSF trust deed&#10;3. Transfer account authorities&#10;4. Update bank signatories&#10;5. Notify service providers&#10;6. Update investment accounts&#10;7. Update insurance policies&#10;8. Complete member notifications"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Document each step required for trustee succession
                        </p>
                    </div>
                    <button
                        onClick={() => {
                            if (transitionSteps.trim()) {
                                alert(`✅ Transition Plan Created!\n\nYour step-by-step succession plan has been saved.\n\nSteps: ${transitionSteps.split('\n').length} documented\n\nThis plan will guide the transition process when needed.`);
                            } else {
                                alert('⚠️ Please enter the transition steps before creating the plan.');
                            }
                        }}
                        className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 font-semibold"
                    >
                        📋 Create Transition Plan
                    </button>
                    
                    {transitionSteps && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-start">
                                <span className="text-2xl mr-3">✅</span>
                                <div>
                                    <div className="font-semibold text-green-800">Plan Status: Documented</div>
                                    <div className="text-sm text-green-700 mt-1">
                                        {transitionSteps.split('\n').filter(s => s.trim()).length} steps documented
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Test 891: Legal Documents */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Legal Documents</h3>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Upload Supporting Documents</label>
                        <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                    setDocuments([...documents, { 
                                        id: Date.now(), 
                                        name: e.target.files[0].name, 
                                        type: e.target.files[0].name.toLowerCase().includes('power') ? 'Power of Attorney' : 
                                              e.target.files[0].name.toLowerCase().includes('will') ? 'Will' : 'Other',
                                        date: new Date().toISOString().split('T')[0] 
                                    }]);
                                    alert(`Document "${e.target.files[0].name}" uploaded successfully`);
                                }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                        <p className="text-xs text-gray-500 mt-1">Power of Attorney, Will, and other succession documents</p>
                    </div>
                    
                    {documents.length > 0 && (
                        <div className="space-y-2">
                            {documents.map((doc) => (
                                <div key={doc.id} className="p-3 bg-gray-50 border border-gray-200 rounded-lg flex justify-between items-center">
                                    <div>
                                        <div className="font-medium">{doc.name}</div>
                                        <div className="text-xs text-gray-500">{doc.type} - Uploaded: {doc.date}</div>
                                    </div>
                                    <button
                                        onClick={() => setDocuments(documents.filter(d => d.id !== doc.id))}
                                        className="text-red-600 hover:text-red-800 text-sm"
                                    >
                                        Delete
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            
            {/* Test 892: Review Schedule */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Review Schedule</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Review Frequency</label>
                        <select
                            value={reviewFrequency}
                            onChange={(e) => setReviewFrequency(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="annual">Annual</option>
                            <option value="biennial">Every 2 Years</option>
                            <option value="triennial">Every 3 Years</option>
                        </select>
                    </div>
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="text-sm text-gray-600 mb-1">Next Review Due</div>
                        <div className="text-lg font-bold text-blue-700">{nextReview}</div>
                        <p className="text-xs text-blue-600 mt-1">Reminder will be set</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SMSFSuccessionPlanning;

